import { SendOfferToAnvaBySessionIdResponse } from 'wg-api-sdk';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { wgApi } from '../../../../app/src/features/api/wgApi';
import { ErrorResponse } from '../../../../../types/API';
import { getJobResult } from './getAnvaQuotesV2';

interface Params {
  queryOptions?: UseMutationOptions<
    SendOfferToAnvaBySessionIdResponse,
    unknown,
    FuncParams
  >;
}

type FuncParams = {
  sessionId?: string;
};

interface JobResponse {
  status: string;
  job_id: string;
  procrastinate_job_id: number;
}

export const postOfferToAnva = async ({
  sessionId,
}: FuncParams): Promise<SendOfferToAnvaBySessionIdResponse> => {
  // Step 1: Post to v2 endpoint to initiate job
  const { data: jobData, isError } = await wgApi.post<JobResponse>(
    `/v2/api/anvaflows/${sessionId}/send-offer-to-anva`,
  );
  if (isError) throw new Error(JSON.stringify(jobData as ErrorResponse));

  // Step 2: Poll for job completion
  const POLL_INTERVAL = 5000; // 5 seconds
  const POLL_TIMEOUT = 300000; // 5 minutes
  const startTime = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const elapsed = Date.now() - startTime;

    if (elapsed >= POLL_TIMEOUT) {
      throw new Error('FLOWS_ANVA_JOB_TIMEOUT_ERROR');
    }

    try {
      const jobResult = await getJobResult(jobData.job_id);

      if (jobResult.status === 'SUCCEEDED') {
        if (jobResult.result) {
          return jobResult.result as unknown as SendOfferToAnvaBySessionIdResponse;
        }
      } else if (jobResult.status === 'FAILED') {
        throw new Error(JSON.stringify(jobResult));
      }

      // Job is still in progress (QUEUED or RUNNING), wait and try again
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    } catch (error) {
      throw new Error(`FLOWS_ANVA_POLL_JOB_ERROR: ${error}`);
    }
  }
};

export const usePostOfferToAnva = ({ queryOptions = {} }: Params) => {
  const queryClient = useQueryClient();

  return useMutation<SendOfferToAnvaBySessionIdResponse, unknown, FuncParams>(
    postOfferToAnva,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'parties',
          'insurance-policies',
          'simulations',
        ]);
      },
      ...queryOptions,
    },
  );
};
